import { useEffect, useState } from "react";
import { View } from "react-native";
import { Button, Card, Input, Text, useTheme } from "~/components/ui";
import {
  cancelDeletion,
  exportMyData,
  getDeletionRequest,
  requestDeletion,
  type DeletionRequest,
} from "~/lib/account-data";
import { isDeletionConfirmed } from "~/lib/account-deletion";
import { spacing } from "~/styles/tokens";
import { saveJsonExport } from "~/utils/export-file";

function messageOf(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * "Export my data" and "Delete account" for a signed-in baker. Deleting asks
 * for the word DELETE and the account's email, then schedules the account for
 * removal. Until that date they stay signed in and can cancel.
 */
export function AccountData({ email }: { email: string }) {
  const { colors } = useTheme();
  const [request, setRequest] = useState<DeletionRequest | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [pending, setPending] = useState<"" | "export" | "delete" | "cancel">(
    "",
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    getDeletionRequest()
      .then((pendingRequest) => {
        if (active) setRequest(pendingRequest);
      })
      .catch(() => {
        // The section still works without it; the request path re-checks.
      });
    return () => {
      active = false;
    };
  }, []);

  const exportData = async () => {
    setPending("export");
    setMessage("");
    try {
      await saveJsonExport(await exportMyData(), "doughbro-account-data.json");
      setMessage("Your data was exported.");
    } catch (error) {
      setMessage(messageOf(error, "Could not export your data."));
    } finally {
      setPending("");
    }
  };

  const confirmDelete = async () => {
    setPending("delete");
    setMessage("");
    try {
      setRequest(await requestDeletion(typedEmail));
      setConfirming(false);
      setPhrase("");
      setTypedEmail("");
    } catch (error) {
      setMessage(messageOf(error, "Could not schedule the deletion."));
    } finally {
      setPending("");
    }
  };

  const cancel = async () => {
    setPending("cancel");
    setMessage("");
    try {
      await cancelDeletion();
      setRequest(null);
    } catch (error) {
      setMessage(messageOf(error, "Could not cancel the deletion."));
    } finally {
      setPending("");
    }
  };

  const confirmed = isDeletionConfirmed({
    phrase,
    email: typedEmail,
    accountEmail: email,
  });

  return (
    <Card elevation="raised">
      <View style={{ gap: spacing.md }}>
        <Text variant="label">Your data</Text>
        <Button
          label={pending === "export" ? "Preparing…" : "Export my data"}
          onPress={() => void exportData()}
          variant="secondary"
          loading={pending === "export"}
          fullWidth
        />

        {request ? (
          <>
            <Text variant="body" color={colors.heading}>
              Your account is scheduled for deletion on{" "}
              {new Date(request.scheduledFor).toLocaleDateString()}. Until then
              you can cancel and keep everything.
            </Text>
            <Button
              label={pending === "cancel" ? "Cancelling…" : "Cancel deletion"}
              onPress={() => void cancel()}
              variant="secondary"
              loading={pending === "cancel"}
              fullWidth
            />
          </>
        ) : confirming ? (
          <>
            <Text variant="body" color={colors.heading}>
              This deletes your account and your starter, logs and timers. It
              happens after 30 days, and you can cancel any time before then.
              Anything stored only on this device is not affected.
            </Text>
            <Input
              label="Type DELETE to confirm"
              value={phrase}
              onChangeText={setPhrase}
              autoCapitalize="characters"
            />
            <Input
              label="Type your email address"
              value={typedEmail}
              onChangeText={setTypedEmail}
              placeholder={email}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
            />
            <Button
              label={pending === "delete" ? "Scheduling…" : "Delete my account"}
              onPress={() => void confirmDelete()}
              variant="danger"
              loading={pending === "delete"}
              disabled={!confirmed || pending !== ""}
              fullWidth
            />
            <Button
              label="Keep my account"
              onPress={() => {
                setConfirming(false);
                setPhrase("");
                setTypedEmail("");
              }}
              variant="ghost"
              disabled={pending !== ""}
              fullWidth
            />
          </>
        ) : (
          <Button
            label="Delete account"
            onPress={() => setConfirming(true)}
            variant="ghost"
            fullWidth
          />
        )}
        {message ? (
          <Text variant="caption" color={colors.heading}>
            {message}
          </Text>
        ) : null}
      </View>
    </Card>
  );
}
